import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { PriceChart } from '../PriceChart';
import { Prediction } from '../../types/predictions';
import { Theme } from '../../theme';
import { useLivePrice } from '../../hooks/useLivePrice';
import { formatPrediction } from '../../utils/priceUtils';

import { ContestEntry } from '../PriceChart/predictions/ContestTargetLine';

// Mini panel positioned on the right side - full screen for clean look
const LandscapeContainer = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for mobile browsers */
  overflow: hidden;
  background: ${props => props.theme.background};
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const ChartSection = styled.div`
  flex: 1;
  height: 100%;
  position: relative;
  min-width: 0; /* Allow flex shrinking */
  display: flex;
  flex-direction: column;
  /* Enable touch for zoom/pan */
  touch-action: pan-x pan-y pinch-zoom;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  /* Enable touch interactions for zoom */
  touch-action: pan-x pan-y pinch-zoom;
  
  /* Force override PriceChart's hardcoded height calculation */
  & > div {
    height: 100% !important;
    max-height: 100% !important;
    min-height: 100% !important;
    touch-action: pan-x pan-y pinch-zoom;
  }
  
  /* Target the styled container inside PriceChart */
  & > div > div {
    height: 100% !important;
    max-height: 100% !important;
  }
`;

const MiniPanel = styled.div`
  width: 140px;
  min-width: 140px;
  height: 100%;
  background: ${props => props.theme.surface};
  border-left: 1px solid ${props => props.theme.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
`;

const PanelHeader = styled.div`
  padding: 6px 8px;
  background: ${props => props.theme.background};
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  min-height: 28px;
`;

const LivePrice = styled.div`
  font-size: 11px;
  font-weight: bold;
  color: ${props => props.theme.primary};
  font-family: 'Courier New', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LiveDot = styled.span<{ isLive: boolean }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${props => props.isLive ? '#22C55E' : '#6B7280'};
  margin-right: 3px;
  display: inline-block;
  flex-shrink: 0;
  ${props => props.isLive && `
    animation: pulse 2s infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
`;

const SearchRow = styled.div`
  padding: 4px;
  border-bottom: 1px solid ${props => props.theme.border};
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 4px 6px;
  font-size: 9px;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: ${props => props.theme.subText};
    opacity: 0.7;
  }
  
  &:focus {
    border-color: ${props => props.theme.primary};
  }
`;

const FilterRow = styled.div`
  display: flex;
  padding: 4px;
  gap: 3px;
  border-bottom: 1px solid ${props => props.theme.border};
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 2px 5px;
  font-size: 8px;
  border-radius: 3px;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.border};
  }
`;

const PredictionsList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Thin scrollbar */
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.background};
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 2px;
  }
`;

const MiniPredictionCard = styled.div<{ isActive: boolean; statusColor: string }>`
  padding: 5px 6px;
  border-bottom: 1px solid ${props => props.theme.border};
  cursor: pointer;
  background: ${props => props.isActive ? `${props.theme.primary}15` : 'transparent'};
  border-left: 2px solid ${props => props.isActive ? props.theme.primary : 'transparent'};
  transition: all 0.15s;
  
  &:hover {
    background: ${props => props.theme.background};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const CardName = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: ${props => props.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 1px;
  max-width: 100%;
`;

const CardPrice = styled.div`
  font-size: 10px;
  font-weight: bold;
  color: ${props => props.theme.primary};
  font-family: 'Courier New', monospace;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
`;

const StatusDot = styled.span<{ status: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props => {
    switch(props.status) {
      case 'success': return '#22C55E';
      case 'miss': return '#EF4444';
      case 'active': return '#3B82F6';
      default: return '#F59E0B';
    }
  }};
`;

const CardDate = styled.span`
  font-size: 8px;
  color: ${props => props.theme.subText};
`;

const ProfileImage = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const NoResults = styled.div`
  padding: 20px 10px;
  text-align: center;
  font-size: 11px;
  color: ${props => props.theme.subText};
`;

// Filter types
type FilterType = 'all' | 'pending' | 'success' | 'miss' | 'bet' | 'sentiment';

interface MobileLandscapeViewProps {
  predictions: Prediction[];
  chartData: Array<{ time: string; value: number }>;
  isDarkMode: boolean;
  theme: Theme;
  asset?: 'btc' | 'mstr';
  // Contest props
  isLoggedIn?: boolean;
  userHandle?: string;
  userContestPrediction?: number | null;
  contestEntries?: ContestEntry[];
  onLogin?: () => void;
  onEntrySubmitted?: (price: number, entry?: any) => void;
}

const MobileLandscapeView: React.FC<MobileLandscapeViewProps> = ({
  predictions,
  chartData,
  isDarkMode,
  theme,
  asset = 'btc',
  isLoggedIn,
  userHandle,
  userContestPrediction,
  contestEntries = [],
  onLogin,
  onEntrySubmitted
}) => {
  const { price: livePrice, loading: priceLoading } = useLivePrice();
  const isConnected = !priceLoading && livePrice !== null;
  const [activePredictions, setActivePredictions] = useState<number[]>([]);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [contestPrice, setContestPrice] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Toggle prediction selection
  const togglePrediction = useCallback((predictionId: number) => {
    setActivePredictions(prev => {
      if (prev.includes(predictionId)) {
        return prev.filter(id => id !== predictionId);
      } else {
        return [...prev, predictionId];
      }
    });
  }, []);

  // Get influencer name (moved up for use in filter)
  const getInfluencerName = (pred: Prediction): string => {
    if (pred.influencer?.name) return pred.influencer.name;
    if (pred.expert_name) return pred.expert_name;
    return 'Unknown';
  };

  // Filter predictions by status AND search query
  const filteredPredictions = useMemo(() => {
    return predictions.filter(pred => {
      // First apply status filter
      if (activeFilter === 'all') { /* pass */ }
      else if (activeFilter === 'pending') {
        if (pred.success_status && pred.success_status !== 'pending') return false;
      }
      else if (activeFilter === 'bet') {
        if (pred.prediction_type !== 'bet') return false;
      }
      else if (activeFilter === 'sentiment') {
        if (pred.prediction_type !== 'sentiment') return false;
      }
      else if (pred.success_status !== activeFilter) return false;
      
      // Then apply search filter
      if (searchQuery.trim()) {
        const name = getInfluencerName(pred).toLowerCase();
        const query = searchQuery.toLowerCase().trim();
        if (!name.includes(query)) return false;
      }
      
      return true;
    }).sort((a, b) => {
      // Sort by nearest upcoming target date (next to resolve first)
      const aTargetDate = a.end_date ? new Date(a.end_date) : (a.target_time ? new Date(a.target_time) : null);
      const bTargetDate = b.end_date ? new Date(b.end_date) : (b.target_time ? new Date(b.target_time) : null);
      
      // Determine if predictions are pending (not yet resolved)
      const aIsPending = !a.success_status || a.success_status === 'pending';
      const bIsPending = !b.success_status || b.success_status === 'pending';
      
      // Pending predictions come before resolved ones
      if (aIsPending && !bIsPending) return -1;
      if (!aIsPending && bIsPending) return 1;
      
      // For pending predictions: sort by nearest upcoming date
      if (aIsPending && bIsPending) {
        if (!aTargetDate && !bTargetDate) return 0;
        if (!aTargetDate) return 1;
        if (!bTargetDate) return -1;
        return aTargetDate.getTime() - bTargetDate.getTime();
      }
      
      // For resolved predictions: most recently resolved first
      if (!aTargetDate && !bTargetDate) return 0;
      if (!aTargetDate) return 1;
      if (!bTargetDate) return -1;
      return bTargetDate.getTime() - aTargetDate.getTime();
    });
  }, [predictions, activeFilter, searchQuery]);

  // Get profile image
  const getProfileImage = (pred: Prediction): string | null => {
    return pred.influencer?.profile_image || null;
  };

  // Format date
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <LandscapeContainer theme={theme}>
      {/* Chart takes most of the space */}
      <ChartSection>
        <ChartWrapper style={{ height: '100%' }}>
          <div style={{ height: '100%', width: '100%' }}>
            <PriceChart
              predictions={predictions}
              activePredictions={activePredictions}
              chartData={chartData}
              isDarkMode={isDarkMode}
              theme={theme}
              compactMode={true}
              userContestPrediction={userContestPrediction}
              userHandle={userHandle}
              contestEntries={contestEntries}
            />
          </div>
        </ChartWrapper>
      </ChartSection>

      {/* Mini prediction panel on the right */}
      <MiniPanel theme={theme}>
        <PanelHeader theme={theme}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <LiveDot isLive={isConnected} />
            <span style={{ fontSize: '10px', color: theme.subText }}>BTC</span>
          </div>
          <LivePrice theme={theme}>
            ${livePrice?.toLocaleString() || '---'}
          </LivePrice>
        </PanelHeader>

        {/* Contest Quick Entry */}
        <div style={{ 
          padding: '6px', 
          borderBottom: `1px solid ${theme.border}`,
          background: 'rgba(247, 147, 26, 0.05)'
        }}>
          {userContestPrediction ? (
            <div style={{ fontSize: '9px', color: theme.text, textAlign: 'center' }}>
              <span style={{ color: '#F7931A' }}>🎯 Your entry:</span> ${userContestPrediction.toLocaleString()}
            </div>
          ) : isLoggedIn ? (
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!contestPrice || isSubmitting) return;
              setIsSubmitting(true);
              try {
                const token = localStorage.getItem('authToken') || localStorage.getItem('jwt_token');
                const cleanPrice = contestPrice.replace(/,/g, '');
                const numericPrice = parseFloat(cleanPrice);
                const res = await fetch('/api/contest/bitcoin2026/enter', {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ price_prediction: numericPrice })
                });
                const data = await res.json();
                if (data.success && onEntrySubmitted) {
                  onEntrySubmitted(numericPrice, data.entry);
                  setContestPrice('');
                }
              } catch (err) {
                console.error('Entry failed:', err);
              }
              setIsSubmitting(false);
            }} style={{ display: 'flex', gap: '4px' }}>
              <input
                type="text"
                placeholder="$"
                value={contestPrice}
                onChange={(e) => setContestPrice(e.target.value)}
                style={{
                  flex: 1,
                  padding: '4px',
                  fontSize: '10px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  background: theme.background,
                  color: theme.text
                }}
              />
              <button type="submit" disabled={isSubmitting} style={{
                padding: '4px 8px',
                fontSize: '9px',
                background: '#F7931A',
                color: '#000',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}>
                {isSubmitting ? '...' : 'Enter'}
              </button>
            </form>
          ) : (
            <button onClick={onLogin} style={{
              width: '100%',
              padding: '4px',
              fontSize: '9px',
              background: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: '4px',
              color: theme.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <svg viewBox="0 0 24 24" fill={theme.text} width="10" height="10">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Sign in to enter contest
            </button>
          )}
        </div>

        <SearchRow theme={theme}>
          <SearchInput
            theme={theme}
            type="text"
            placeholder="🔍 Search influencer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchRow>

        <FilterRow theme={theme}>
          {(['all', 'pending', 'success', 'miss'] as FilterType[]).map(filter => (
            <FilterButton
              key={filter}
              active={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
              theme={theme}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </FilterButton>
          ))}
        </FilterRow>

        <PredictionsList theme={theme}>
          {filteredPredictions.length === 0 ? (
            <NoResults theme={theme}>No predictions</NoResults>
          ) : (
            filteredPredictions.map(pred => {
              const isActive = activePredictions.includes(pred.id);
              const status = pred.success_status || 'pending';
              const profileImg = getProfileImage(pred);
              
              return (
                <MiniPredictionCard
                  key={pred.id}
                  isActive={isActive}
                  statusColor={status}
                  theme={theme}
                  onClick={() => togglePrediction(pred.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {profileImg && <ProfileImage src={profileImg} alt="" />}
                    <CardName theme={theme}>{getInfluencerName(pred)}</CardName>
                  </div>
                  <CardPrice theme={theme}>{formatPrediction(pred)}</CardPrice>
                  <CardMeta>
                    <StatusDot status={status} />
                    <CardDate theme={theme}>{formatDate(pred.target_time)}</CardDate>
                  </CardMeta>
                </MiniPredictionCard>
              );
            })
          )}
        </PredictionsList>
      </MiniPanel>
    </LandscapeContainer>
  );
};

export default MobileLandscapeView;
